import axios from 'axios';
import { API } from '../global/global';

// Interfaces
export interface VariableProducto {
  id: number;
  plataforma_id: number;
  producto: string;
  variables: string[];
  activo: boolean;
  plataforma?: {
    id: number;
    nombre: string;
  };
  created_at: string;
  updated_at: string;
}

export interface Producto {
  id: number;
  variable_producto_id: number;
  tagblac_id?: number;
  tag_tracksolid_id?: number;
  datos: string[];
  activo: boolean;
  tipo_producto?: string;
  datos_mapeados?: { [key: string]: string };
  tag_blac?: any;
  tag_tracksolid?: any;
  plataforma_nombre?: string;
  created_at: string;
  updated_at: string;
}

export interface ProductoConUbicacion {
  id: number;
  tipo_producto: string;
  datos: any;
  tag_plate: string;
  tag_name_device: string;
  latitude: string;
  longitude: string;
  tag_fuente?: 'blac' | 'tracksolid';
  tag_tracksolid_id?: number;
  created_at: string;
  plataforma: {
    id: number;
    nombre: string;
    slug: string;
  };
}

export interface TipoProducto {
  id: number;
  producto: string;
}

export interface DefinicionProducto {
  id: number;
  producto: string;
  variables: string[];
}

// API para Variables Productos (Tipos de productos)
export const variablesProductosApi = {
  // Obtener todos los tipos de productos para la plataforma del usuario logueado
  getTipos: async (): Promise<TipoProducto[]> => {
    // Obtener plataforma del localStorage (viene del AuthContext)
    const plataformaData = localStorage.getItem('plataforma');
    const plataforma = plataformaData ? JSON.parse(plataformaData) : null;
    
    if (!plataforma?.id) {
      throw new Error('No se pudo obtener la información de la plataforma del usuario');
    }
    
    const response = await axios.get(`${API}/productos-dinamicos/tipos`, {
      params: { plataforma_id: plataforma.id },
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Obtener definición de un tipo de producto
  getDefinicion: async (id: number): Promise<DefinicionProducto> => {
    const response = await axios.get(`${API}/productos-dinamicos/tipos/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Crear nuevo tipo de producto (usa plataforma del usuario automáticamente)
  crear: async (data: {
    producto: string;
    variables: string[];
  }): Promise<VariableProducto> => {
    // Obtener plataforma del localStorage (viene del AuthContext)
    const plataformaData = localStorage.getItem('plataforma');
    const plataforma = plataformaData ? JSON.parse(plataformaData) : null;
    
    if (!plataforma?.id) {
      throw new Error('No se pudo obtener la información de la plataforma del usuario');
    }
    
    const dataWithPlatform = {
      ...data,
      plataforma_id: plataforma.id
    };
    
    const response = await axios.post(`${API}/variables-productos`, dataWithPlatform, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Actualizar tipo de producto
  actualizar: async (id: number, data: {
    producto: string;
    variables: string[];
  }): Promise<VariableProducto> => {
    const response = await axios.put(`${API}/variables-productos/${id}`, data, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Obtener todos los tipos de productos para administración (usa plataforma del usuario)
  getAll: async (): Promise<VariableProducto[]> => {
    // Obtener plataforma del usuario desde localStorage
    const usuario = localStorage.getItem('usuario');
    const plataformaId = usuario ? JSON.parse(usuario).plataforma_id : null;
    
    const response = await axios.get(`${API}/variables-productos`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'X-Plataforma-Id': plataformaId
      }
    });
    return response.data.data;
  },

  // Eliminar tipo de producto
  eliminar: async (id: number): Promise<void> => {
    await axios.delete(`${API}/variables-productos/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
  }
};

// API para Productos
export const productosApi = {
  // Obtener tags BLAC disponibles (no asignadas)
  getTagsDisponibles: async (): Promise<any[]> => {
    const response = await axios.get(`${API}/productos-dinamicos/tags-disponibles`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Obtener todos los productos
  getAll: async (params?: {
    variable_producto_id?: number;
    buscar?: string;
    per_page?: number;
    page?: number;
  }): Promise<{
    data: Producto[];
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  }> => {
    const response = await axios.get(`${API}/productos-dinamicos/filtrados`, {
      params,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Obtener un producto específico
  getById: async (id: number): Promise<Producto> => {
    const response = await axios.get(`${API}/productos-dinamicos/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Crear nuevo producto
  crear: async (data: {
    variable_producto_id: number;
    tagblac_id?: number;
    datos: string[];
  }): Promise<Producto> => {
    const response = await axios.post(`${API}/productos-dinamicos`, data, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Actualizar producto
  actualizar: async (id: number, data: {
    tagblac_id?: number;
    datos: string[];
  }): Promise<Producto> => {
    const response = await axios.put(`${API}/productos-dinamicos/${id}`, data, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  },

  // Eliminar producto
  eliminar: async (id: number): Promise<void> => {
    await axios.delete(`${API}/productos-dinamicos/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
  },

  // Obtener productos con ubicación para el mapa (filtrados por plataforma)
  getProductosConUbicacion: async (): Promise<ProductoConUbicacion[]> => {
    const response = await axios.get(`${API}/productos-dinamicos/con-ubicacion`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  }
};
